/* eslint-disable complexity */
import {
	checkValidation,
	CustomerService,
	ICustomer,
	OfferContext,
	ITypeVignettenCheckoutFields,
	VignetteContext,
} from '@adac-mp/shared';
import React, { ChangeEvent, useContext, useState } from 'react';
import { Input, Discount, Typography } from '../../index';
import { usePreviousValue } from '../../hooks/usePreviousValue';
import { useValidatedStates } from '../../hooks/useValidatedStates';

interface IMemberInputProps {
	className?: string;
	customer?: ICustomer;
	discount?: string;
	hasMembershipBenefit?: boolean;
	setMemberSuccess: (active: boolean) => void;
	setCustomer: (member: ICustomer) => void;
	updateMembership?: (isMember: boolean) => void;
}

export const MemberInput = ({
	className = '',
	customer,
	discount,
	hasMembershipBenefit = false,
	setMemberSuccess,
	updateMembership = () => {},
	setCustomer,
}: IMemberInputProps) => {
	const { lang, allOffers, updateVignetteOffers } = useContext(OfferContext);
	const { contentfulLabels } = useContext(VignetteContext);
	const initState = { memberNumber: { value: customer?.person?.memberNo, error: false } };
	const [state, { update, updateWithError }] = useValidatedStates(initState);
	const [isLoading, setIsLoading] = useState(false);
	const [isOnBlur, setIsOnBlur] = useState(true);
	const previousMemberValue = usePreviousValue(state.memberNumber.value);
	const currentLabels = contentfulLabels as ITypeVignettenCheckoutFields;

	const updateCustomersMembership = (isMember: boolean) => {
		setMemberSuccess(isMember || !state.memberNumber.value);
	};

	const setMemberCustomer = (value: string) => {
		if (customer) {
			const memberCustomer = {
				...customer,
				person: {
					...customer.person,
					memberNo: value,
				},
			};
			setCustomer(memberCustomer);
		}
	};

	const handleMemberNumberResponse = (isMember: boolean) => {
		setMemberCustomer(isMember ? state.memberNumber.value : '');
		if (hasMembershipBenefit) {
			const updatedVignetteOffers = allOffers.filter((offer) => (isMember ? offer.memberYes : offer.memberNo));
			updateVignetteOffers(updatedVignetteOffers);
			updateCustomersMembership(isMember);
			updateMembership(isMember);
		}
	};

	const checkMemberNumber = async () => {
		if (previousMemberValue === state.memberNumber.value) {
			return;
		}
		setIsLoading(true);
		let isMember = false;
		if (state.memberNumber.value) {
			const { code } = await CustomerService.checkMemberNo({ memberNo: state.memberNumber.value }, lang);
			isMember = code === 200;
		}
		handleMemberNumberResponse(isMember);
		updateWithError('memberNumber', state.memberNumber.value, !isMember);
		setIsOnBlur(true);
		setIsLoading(false);
	};

	const handleValueChanged = (e: ChangeEvent<HTMLInputElement>) => {
		setIsOnBlur(false);
		const value = e.target.value;
		if (value === '' || (!checkValidation(value, 'INTEGERS') && value.length <= 9)) {
			update('memberNumber', value);
		}
	};

	const getHelperText = () => {
		if (state.memberNumber.value && state.memberNumber.error) {
			if (state.memberNumber.value.length < 9) {
				return (
					currentLabels?.personalData?.memberNumberLengthError ||
					'Keine gültige Mitgliedsnummer. Bitte geben Sie eine 9-stellige Mitgliedsnummer ein.'
				);
			}
			return (
				currentLabels?.personalData?.membershipNumberInvalidError ||
				'Diese Mitgliedsnummer ist uns leider nicht bekannt. Bitte korrigieren Sie Ihre Eingabe oder kontaktieren Sie uns.'
			);
		}
		if (hasMembershipBenefit && discount) {
			return (
				currentLabels?.personalData?.membershipBenefit?.replace('{discount}', discount) ||
				`${`Ihr ADAC Vorteil: ${discount}€ Servicerabatt wurden soeben abgezogen`}.`
			);
		}
		return '';
	};

	return hasMembershipBenefit ? (
		<form
			className="bg-lightBlue rounded px-1.5 pt-3.5 border-solid border-2 border-black border-opacity-[0.04]"
			autoComplete="off"
			onSubmit={(ev) => {
				ev.preventDefault();
			}}>
			<div className="px-3 mb-4">
				<Typography variant="title" size="lgEven" color="title" className="font-bold mb-1.5" component="p">
					{currentLabels?.personalData?.membershipBenefitHeadline || 'ADAC Vorteil sichern'}
				</Typography>
				<Typography color="title">
					{currentLabels?.personalData?.memberBenefitDescription ||
						'Geben Sie Ihre Mitgliedsnummer ein, um einen Servicerabatt zu erhalten.'}
				</Typography>
			</div>
			<Input
				data-cy="input: memberInput"
				className={className}
				wrapperStyle={{ marginBottom: 8 }}
				helperTextStyle={{ paddingBottom: 8 }}
				placeholder={currentLabels?.personalData?.memberNumber || 'ADAC-Mitgliedsnummer'}
				value={state.memberNumber.value ?? ''}
				icon={<Discount />}
				disabled={isLoading}
				loading={isLoading}
				hasError={isOnBlur && state.memberNumber.value && state.memberNumber.error}
				hasSucceeded={isOnBlur && state.memberNumber.value && !state.memberNumber.error}
				autocomplete="off"
				size="mdEven"
				helperText={getHelperText()}
				onChange={handleValueChanged}
				onBlur={checkMemberNumber}
			/>
		</form>
	) : (
		<Input
			data-cy="input: memberInput"
			className={className}
			wrapperStyle={{ marginBottom: 8, marginTop: 32 }}
			helperTextStyle={{ paddingBottom: 8 }}
			placeholder={currentLabels?.personalData?.memberNumber || 'ADAC-Mitgliedsnummer'}
			value={state.memberNumber.value ?? ''}
			icon={<Discount />}
			disabled={isLoading}
			loading={isLoading}
			hasError={isOnBlur && state.memberNumber.value && state.memberNumber.error}
			checked={isOnBlur && state.memberNumber.value && !state.memberNumber.error}
			autocomplete="off"
			helperText={getHelperText()}
			onChange={handleValueChanged}
			onBlur={checkMemberNumber}
		/>
	);
};
